import type { AppConfig, AppConfigOverrides } from '@shared/app-config'
import type { ProxyConfig } from '@shared/wippy-package/wippy-package'
import { processWebPage } from '@shared/api/web-components'
import { importMap, scripts } from '@shared/api/web-components/constants'
import { getAppConfig } from '@shared/app-config'
import { buildChildPayload } from '@shared/app-config/migration'

export interface HtmlInjectOptions {
  baseUrl?: string
  resourceId?: string
  resourceType?: AppConfig['context']['resourceType']
  route?: string
  isNavOwner?: boolean
  configOverrides?: AppConfigOverrides
  proxyConfig?: ProxyConfig
}

export interface ProxyHtmlApi {
  inject: (sourceHtml: string, options?: HtmlInjectOptions) => Promise<string>
}

/**
 * Normalize a route string into the canonical form the proxy uses
 * internally — undefined for "no route", `/` for empty, leading slash
 * guaranteed otherwise. Exported so `w-iframe` can reuse it without
 * duplicating the helper.
 */
export function normalizeRoute(route: string | null | undefined) {
  if (route === null || route === undefined)
    return undefined
  if (route === '')
    return '/'
  return route.startsWith('/') ? route : `/${route}`
}

export function createHtmlAPI(): ProxyHtmlApi {
  return {
    inject(sourceHtml, options = {}) {
      return injectWippyHtml(sourceHtml, options)
    },
  }
}

export function buildInjectedChildConfig(options: HtmlInjectOptions = {}) {
  const parentConfig = getAppConfig()
  const parentContext = parentConfig.context
  return buildChildPayload(
    parentConfig,
    {
      resourceId: options.resourceId || '',
      resourceType: options.resourceType || 'page',
      route: normalizeRoute(options.route),
      parentResourceId: parentContext?.resourceId || undefined,
      nestingDepth: (parentContext?.nestingDepth ?? 0) + 1,
      isNavOwner: options.isNavOwner || undefined,
    },
    options.configOverrides,
  )
}

export function injectWippyHtml(sourceHtml: string, options: HtmlInjectOptions = {}) {
  return processWebPage(sourceHtml, {
    scripts,
    importMap,
    baseUrl: options.baseUrl,
    appConfig: buildInjectedChildConfig(options),
    proxyConfig: options.proxyConfig,
    forceAppConfig: true,
  })
}
